/**
 * Workout tracker: logging, backup/restore, and DOM wiring.
 *
 * State and persistence live in lib/store.ts; the individual screens live in
 * views/. This module owns the log form, the export/import flow, and the
 * event wiring that connects the markup to all of it.
 */
import * as Sync from './sync';
import { input, maybe, must, onClick, select } from './dom';
import { escapeHtml, getExerciseDisplayName } from './lib/format';
import {
  currentExercise,
  exercises,
  metricsFor,
  onRefresh,
  persistLocal,
  refreshAllViews,
  saveLocal,
  setCurrentExercise,
  syncStore,
  workoutData,
} from './lib/store';
import { showCalendarView, updateCalendarView } from './views/calendar';
import {
  addNewExercise,
  createNewVariant,
  hideAddExerciseModal,
  hideEditExerciseModal,
  hideMetricUpdateModal,
  showAddExerciseModal,
  showEditExerciseModal,
  updateExercise,
  updateExistingExercise,
} from './views/exercises';
import { applyMetric, buildMetricField, readMetricInput } from './views/fields';
import {
  confirmDeleteWorkout,
  deleteWorkout,
  displayWorkoutHistory,
  editWorkout,
  hideDeleteWorkoutModal,
  hideEditWorkoutModal,
  resetHistoryExpansion,
  saveWorkoutEdit,
  toggleHistoryExpansion,
} from './views/history';
import {
  showChartView,
  showStatsView,
  updateProgressView,
  updateStats,
} from './views/stats';
import { localStamp, type ExportFile, type Workout } from './types';

const EXPORT_VERSION = '3.0';

let importedData: ExportFile | null = null;

export { persistLocal, refreshAllViews, syncStore as store };

// --- exercise selector -------------------------------------------------

function populateExerciseSelector(): void {
  const selector = select('exercise-select');
  selector.innerHTML = '';

  for (const exercise of exercises) {
    const option = document.createElement('option');
    option.value = exercise.name;
    option.textContent = getExerciseDisplayName(exercise.name);
    selector.appendChild(option);
  }

  selector.value = currentExercise;
  updateEditExerciseLink();
}

function updateEditExerciseLink(): void {
  const editLink = maybe('edit-exercise-link');
  if (!editLink) return;

  let baseName = currentExercise;
  const parenIndex = baseName.indexOf('(');
  if (parenIndex !== -1) baseName = baseName.substring(0, parenIndex).trim();
  baseName = baseName.charAt(0).toUpperCase() + baseName.slice(1);

  let settingsText = `${baseName} Settings`;
  if (settingsText.length > 18) settingsText = `${settingsText.substring(0, 15)}...`;

  editLink.textContent = `⚙️ ${settingsText}`;
}

// --- logging a workout --------------------------------------------------

function updateWorkoutInputs(): void {
  const container = document.querySelector<HTMLElement>('.workout-input');
  if (!container) return;

  container.innerHTML = '';

  for (const metric of metricsFor(currentExercise)) {
    const wrapper = buildMetricField(metric, '', false);
    const field = wrapper.querySelector('input');
    if (field) {
      field.addEventListener('focus', () => {
        field.style.borderColor = '#667eea';
      });
      field.addEventListener('blur', () => {
        field.style.borderColor = '#e0e0e0';
      });
      field.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') logWorkout();
      });
    }
    container.appendChild(wrapper);
  }
}

function logWorkout(): void {
  const metrics = metricsFor(currentExercise);
  const workout: Workout = {
    id: '',
    date: new Date().toISOString(),
    exercise: currentExercise,
    clientUpdated: localStamp(''),
  };

  let hasValidInput = false;
  for (const metric of metrics) {
    const value = readMetricInput('', metric);
    if (value === null) continue;
    applyMetric(workout, metric, value);
    hasValidInput = true;
  }

  if (!hasValidInput) {
    alert('Please enter valid values for at least one metric');
    return;
  }

  Sync.stampWorkout(workout);
  (workoutData[currentExercise] ??= []).push(workout);
  saveLocal();

  for (const metric of metrics) {
    const field = maybe<HTMLInputElement>(`${metric}-input`);
    if (field) field.value = '';
  }

  updateStats();
  displayWorkoutHistory();
  updateProgressView();
  updateCalendarView();

  const button = maybe<HTMLButtonElement>('log-workout-btn');
  if (button) {
    const originalText = button.textContent;
    button.textContent = 'Logged! 🎉';
    button.style.background = 'rgb(79, 172, 254)';
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }, 2000);
  }
}

// --- tabs ----------------------------------------------------------------

function showTab(tabName: 'progress' | 'calendar', button: HTMLElement): void {
  for (const btn of document.querySelectorAll('.tab-button')) btn.classList.remove('active');
  button.classList.add('active');

  for (const content of document.querySelectorAll('.tab-content')) {
    content.classList.remove('active');
  }
  must(`${tabName}-tab`).classList.add('active');

  if (tabName === 'progress') updateProgressView();
  // Deferred to the moment the tab opens: the graph cannot be scrolled to its
  // right-hand end while it is still display:none and has no scroll width.
  if (tabName === 'calendar') showCalendarView();
}

// --- backup and restore ---------------------------------------------------

function exportData(): void {
  const payload: ExportFile = {
    exercises,
    workoutData,
    exportDate: new Date().toISOString(),
    version: EXPORT_VERSION,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const today = new Date().toISOString().split('T')[0] ?? 'export';
  const fileName = `workout-data-${today}.json`;

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  const total = Object.values(workoutData).reduce((sum, list) => sum + (list?.length ?? 0), 0);

  alert(
    `✅ Backup successful!\n\nFile: ${fileName}\nExercises: ${exercises.length}\n` +
      `Total workouts: ${total}\n\n📱 Mobile tip: Check your Downloads folder or look ` +
      `for the file in your device's file manager.\n\n💡 Remember to email this file to ` +
      `yourself for extra backup!`,
  );
}

function importData(event: Event): void {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => {
    try {
      const data = JSON.parse(String(reader.result)) as ExportFile;

      if (!data.exercises || !data.workoutData || !data.version) {
        alert('Invalid file format. This file may be from an incompatible version.');
        return;
      }
      if (data.version !== EXPORT_VERSION) {
        alert(
          `Unsupported file version: ${data.version}. ` +
            `This app supports version ${EXPORT_VERSION} files only.`,
        );
        return;
      }

      importedData = data;

      const total = Object.values(data.workoutData).reduce(
        (sum, list) => sum + (list?.length ?? 0),
        0,
      );
      const exportDate = data.exportDate
        ? new Date(data.exportDate).toLocaleDateString()
        : 'Unknown';

      must('import-message').innerHTML =
        '<strong>Import Preview:</strong><br>' +
        `• ${data.exercises.length} exercises<br>` +
        `• ${total} total workouts<br>` +
        `• Export date: ${escapeHtml(exportDate)}<br>` +
        `• Version: ${escapeHtml(data.version)}<br><br>` +
        'This will replace all your current data. Continue?';

      must('import-modal').style.display = 'block';
    } catch {
      alert('Error reading file. Please make sure this is a valid workout data backup.');
    }
  };

  reader.readAsText(file);
  target.value = '';
}

function hideImportModal(): void {
  must('import-modal').style.display = 'none';
  importedData = null;
}

function confirmImport(): void {
  if (!importedData) return;

  syncStore.setExercises(importedData.exercises);
  syncStore.setWorkoutData(importedData.workoutData);

  // Imported records carry no sync ids; stamp them so they upload as new
  // records instead of being skipped.
  Sync.ensureStamped();
  saveLocal();

  refreshAllViews();
  hideImportModal();
  alert('Data imported successfully! 📥');
}

// --- wiring ---------------------------------------------------------------

function redrawEverything(): void {
  if (!exercises.some((ex) => ex.name === currentExercise) && exercises[0]) {
    setCurrentExercise(exercises[0].name);
  }
  populateExerciseSelector();
  updateWorkoutInputs();
  updateStats();
  displayWorkoutHistory();
  updateProgressView();
  updateCalendarView();
  updateEditExerciseLink();
}

function bindModalDismiss(id: string, hide: () => void): void {
  const modal = must(id);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) hide();
  });
}

export function initApp(): void {
  onRefresh(redrawEverything);

  select('exercise-select').addEventListener('change', (event) => {
    setCurrentExercise((event.target as HTMLSelectElement).value);
    resetHistoryExpansion();
    updateStats();
    displayWorkoutHistory();
    updateProgressView();
    updateWorkoutInputs();
    updateEditExerciseLink();
  });

  onClick('add-exercise-link', showAddExerciseModal);
  onClick('edit-exercise-link', showEditExerciseModal);
  onClick('log-workout-btn', logWorkout);

  must('tab-progress').addEventListener('click', (event) => {
    showTab('progress', event.currentTarget as HTMLElement);
  });
  must('tab-calendar').addEventListener('click', (event) => {
    showTab('calendar', event.currentTarget as HTMLElement);
  });

  onClick('stats-view-btn', showStatsView);
  onClick('chart-view-btn', () => void showChartView());
  onClick('history-toggle', toggleHistoryExpansion);

  onClick('export-btn', exportData);
  onClick('import-btn', () => input('import-file').click());
  input('import-file').addEventListener('change', importData);

  onClick('add-exercise-cancel', hideAddExerciseModal);
  onClick('add-exercise-confirm', addNewExercise);
  onClick('edit-exercise-cancel', hideEditExerciseModal);
  onClick('edit-exercise-confirm', updateExercise);
  onClick('metric-update-existing', updateExistingExercise);
  onClick('metric-update-variant', createNewVariant);
  onClick('metric-update-cancel', hideMetricUpdateModal);
  onClick('edit-workout-cancel', hideEditWorkoutModal);
  onClick('edit-workout-confirm', saveWorkoutEdit);
  onClick('delete-workout-cancel', hideDeleteWorkoutModal);
  onClick('delete-workout-confirm', confirmDeleteWorkout);
  onClick('import-cancel', hideImportModal);
  onClick('import-confirm', confirmImport);

  // Delegated: history rows are rebuilt on every render.
  must('workout-history').addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>('button[data-action]');
    const id = button?.dataset['workoutId'];
    if (!button || !id) return;
    if (button.dataset['action'] === 'edit') editWorkout(id);
    else deleteWorkout(id);
  });

  bindModalDismiss('add-exercise-modal', hideAddExerciseModal);
  bindModalDismiss('import-modal', hideImportModal);
  bindModalDismiss('edit-workout-modal', hideEditWorkoutModal);
  bindModalDismiss('delete-workout-modal', hideDeleteWorkoutModal);
  bindModalDismiss('edit-exercise-modal', hideEditExerciseModal);
  bindModalDismiss('metric-update-modal', hideMetricUpdateModal);

  input('new-exercise-name').addEventListener('keypress', (event) => {
    if (event.key === 'Enter') addNewExercise();
  });

  redrawEverything();
}
